import React, { useState, useCallback } from 'react';
import { IconButton, Button, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmationDialog from './ConfirmationDialog';
import { isWithinEditWindow, describeEditWindow } from '../utils/isWithinEditWindow';

const DELETE_TOOLTIP = 'Delete claim';

export interface DeleteClaimButtonProps {
  editableUntil?: string;
  /** The configured EncounterPortalEditWindow duration in minutes (from GET /api/v1/encounters/config).
   *  Undefined while the config hasn't loaded yet — messages fall back to generic wording. */
  windowMinutes?: number;
  hasMultipleCases: boolean;
  onDelete: () => Promise<void>;
  onDeleted?: () => void;
  onError?: (message: string) => void;
  onIsDeleting?: (v: boolean) => void;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

const DeleteClaimButton = React.memo(function DeleteClaimButton({
  editableUntil,
  windowMinutes,
  hasMultipleCases,
  onDelete,
  onDeleted,
  onError,
  onIsDeleting,
  variant = 'button',
  disabled = false,
}: DeleteClaimButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPastWindow = !isWithinEditWindow(editableUntil);
  const isDisabled = isPastWindow || disabled || isDeleting;
  const windowDescription = describeEditWindow(windowMinutes);
  const expiredTooltip = `Deletion not allowed after the ${windowDescription} closes`;
  const expiredMessage = `This encounter's ${windowDescription} has expired. The claim was not deleted.`;

  const setDeleting = useCallback(
    (v: boolean) => {
      setIsDeleting(v);
      onIsDeleting?.(v);
    },
    [onIsDeleting],
  );

  const handleConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await onDelete();
      setDeleting(false);
      onDeleted?.();
    } catch (err) {
      let message: string;
      const ax = err as {
        response?: { status?: number; data?: { error?: string; message?: string } };
      };
      if (
        ax?.response?.status === 409 &&
        ax?.response?.data?.error === 'EDIT_WINDOW_EXPIRED'
      ) {
        message = expiredMessage;
      } else if (ax?.response?.data?.message) {
        message = ax.response.data.message;
      } else if (err instanceof Error) {
        message = err.message;
      } else {
        message = 'Failed to delete claim. Please try again.';
      }
      setDeleting(false);
      setDialogOpen(false);
      onError?.(message);
    }
  }, [onDelete, onDeleted, onError, setDeleting, expiredMessage]);

  const button =
    variant === 'icon' ? (
      <IconButton
        aria-label="Delete claim"
        color="error"
        onClick={() => setDialogOpen(true)}
        disabled={isDisabled}
      >
        <DeleteOutlineIcon />
      </IconButton>
    ) : (
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteOutlineIcon />}
        onClick={() => setDialogOpen(true)}
        disabled={isDisabled}
        sx={{ fontSize: 12, fontWeight: 500, px: 3, height: 36 }}
      >
        Delete claim
      </Button>
    );

  return (
    <>
      <Tooltip title={isPastWindow ? expiredTooltip : DELETE_TOOLTIP}>
        <span>{button}</span>
      </Tooltip>
      <ConfirmationDialog
        open={dialogOpen}
        title="Delete claim?"
        message={
          hasMultipleCases
            ? 'This encounter holds multiple claims. Deleting it removes the encounter and all of its claims. This action cannot be undone.'
            : 'Are you sure you want to delete this claim? This action cannot be undone.'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={isDeleting}
        onConfirm={handleConfirm}
        onCancel={() => setDialogOpen(false)}
      />
    </>
  );
});

export default DeleteClaimButton;
